import React, { useState, useEffect } from 'react';
import {
  X,
  Mail,
  Phone,
  Calendar,
  Briefcase,
  Brain,
  Edit2,
  Star,
  Clock,
  FileText,
  Upload,
  Plus,
  ChevronRight,
  User,
  TrendingUp,
  CheckCircle,
  AlertCircle,
  FileIcon,
  Download,
} from 'lucide-react';

const COLORS = {
  primary: '#2E8B98',
  success: '#62BB46',
  warning: '#F26522',
  danger: '#DC3545',
  background: '#F8F9FA',
  text: '#212529',
};

const SHADOWS = {
  neumorphic: '5px 5px 10px rgba(186,199,216,0.45), -5px -5px 10px rgba(255,255,255,0.8)',
  inset: 'inset 3px 3px 6px rgba(186,199,216,0.45), inset -3px -3px 6px rgba(255,255,255,0.8)',
  modal: '12px 12px 24px rgba(186,199,216,0.65), -12px -12px 24px rgba(255,255,255,0.9)',
};

const JOB_CATEGORY_COLORS = {
  doctor: '#E74C3C',
  nurse: '#3498DB',
  admin: '#9B59B6',
  staff: '#2ECC71',
  management: '#F39C12',
};

// Utility: Calculate tenure
const calculateTenure = (hireDate) => {
  if (!hireDate) return 'N/A';
  const now = new Date();
  const hire = new Date(hireDate);
  let years = now.getFullYear() - hire.getFullYear();
  let months = now.getMonth() - hire.getMonth();
  if (months < 0) {
    years--;
    months += 12;
  }
  return `${years}y ${months}m`;
};

// Utility: Format date
const formatDate = (date) => {
  if (!date) return 'N/A';
  return new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

// Utility: Get initials
const getInitials = (firstName, lastName) => {
  return `${(firstName || 'U')[0]}${(lastName || 'N')[0]}`.toUpperCase();
};

// Utility: Get category color
const getCategoryColor = (jobTitle) => {
  const title = (jobTitle || '').toLowerCase();
  if (title.includes('doctor') || title.includes('physician')) return JOB_CATEGORY_COLORS.doctor;
  if (title.includes('nurse')) return JOB_CATEGORY_COLORS.nurse;
  if (title.includes('admin')) return JOB_CATEGORY_COLORS.admin;
  if (title.includes('manager') || title.includes('director')) return JOB_CATEGORY_COLORS.management;
  return JOB_CATEGORY_COLORS.staff;
};

// Tab: Overview
const OverviewTab = ({ person }) => {
  const jobColor = getCategoryColor(person.jobTitle);
  return (
    <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Quick summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        {[
          { label: 'Title', value: person.jobTitle || 'N/A' },
          { label: 'Department', value: person.department || 'N/A' },
          { label: 'Status', value: person.status || 'Active' },
          { label: 'Tenure', value: calculateTenure(person.hireDate) },
        ].map((item, i) => (
          <div
            key={i}
            style={{
              padding: '16px',
              borderRadius: '12px',
              backgroundColor: COLORS.background,
              boxShadow: SHADOWS.inset,
              display: 'flex',
              flexDirection: 'column',
              gap: '6px',
            }}
          >
            <span style={{ fontSize: '12px', color: '#666', fontWeight: '600', textTransform: 'uppercase' }}>
              {item.label}
            </span>
            <span style={{ fontSize: '14px', fontWeight: '600', color: COLORS.text }}>
              {item.value}
            </span>
          </div>
        ))}
      </div>

      {/* About section */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <h3 style={{ fontSize: '14px', fontWeight: '700', color: COLORS.text, margin: 0 }}>About</h3>
        <div
          style={{
            padding: '16px',
            borderRadius: '12px',
            backgroundColor: COLORS.background,
            boxShadow: SHADOWS.inset,
            fontSize: '14px',
            color: '#555',
            lineHeight: '1.6',
          }}
        >
          {person.notes || 'No additional notes on file.'}
        </div>
      </div>

      {/* Key metrics */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <h3 style={{ fontSize: '14px', fontWeight: '700', color: COLORS.text, margin: 0 }}>Key Metrics</h3>
        {[
          { label: 'Attendance Rate', value: '96%' },
          { label: 'Performance Score', value: '4.2/5.0' },
          { label: 'Leave Balance', value: '12 days' },
        ].map((metric, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid #e0e0e0' }}>
            <span style={{ fontSize: '13px', color: '#666' }}>{metric.label}</span>
            <span style={{ fontSize: '14px', fontWeight: '600', color: COLORS.primary }}>{metric.value}</span>
          </div>
        ))}
      </div>

      {/* AI summary placeholder */}
      <div style={{ padding: '16px', borderRadius: '12px', backgroundColor: '#F0F8FF', border: `1px solid ${COLORS.primary}30`, display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
        <Brain size={18} style={{ color: COLORS.primary, marginTop: '2px', flexShrink: 0 }} />
        <div>
          <p style={{ margin: '0 0 6px 0', fontSize: '12px', fontWeight: '600', color: COLORS.primary }}>AI Insights</p>
          <p style={{ margin: 0, fontSize: '13px', color: '#555', lineHeight: '1.5' }}>
            AI-generated summary coming soon. This will provide insights based on performance, attendance, and engagement data.
          </p>
        </div>
      </div>
    </div>
  );
};

// Tab: Employment History
const EmploymentHistoryTab = ({ person }) => {
  const events = [
    { date: person.hireDate, type: 'Hired', description: `Hired as ${person.jobTitle}` },
    { date: '2023-06-15', type: 'Promotion', description: 'Promoted to Senior Role' },
    { date: '2024-01-10', type: 'Transfer', description: `Transferred to ${person.department}` },
  ].filter(e => e.date);

  return (
    <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <div style={{ position: 'relative', paddingLeft: '40px' }}>
        {events.map((event, i) => (
          <div key={i} style={{ marginBottom: i < events.length - 1 ? '32px' : 0, position: 'relative' }}>
            {/* Timeline line */}
            {i < events.length - 1 && (
              <div
                style={{
                  position: 'absolute',
                  left: '-24px',
                  top: '24px',
                  width: '2px',
                  height: '32px',
                  backgroundColor: '#e0e0e0',
                }}
              />
            )}
            {/* Timeline dot */}
            <div
              style={{
                position: 'absolute',
                left: '-32px',
                top: '0px',
                width: '16px',
                height: '16px',
                borderRadius: '50%',
                backgroundColor: COLORS.primary,
                border: '3px solid white',
                boxShadow: SHADOWS.neumorphic,
              }}
            />
            {/* Event card */}
            <div
              style={{
                padding: '16px',
                borderRadius: '12px',
                backgroundColor: COLORS.background,
                boxShadow: SHADOWS.inset,
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '12px', fontWeight: '600', color: '#666' }}>
                  {formatDate(event.date)}
                </span>
                <span
                  style={{
                    padding: '2px 8px',
                    borderRadius: '6px',
                    backgroundColor: COLORS.primary + '20',
                    fontSize: '11px',
                    fontWeight: '600',
                    color: COLORS.primary,
                  }}
                >
                  {event.type}
                </span>
              </div>
              <p style={{ margin: 0, fontSize: '14px', color: COLORS.text, fontWeight: '500' }}>
                {event.description}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Tab: Performance & Reviews
const PerformanceTab = ({ person }) => {
  const rating = 4.2;
  const maxRating = 5;
  const goals = [
    { title: 'Complete training', progress: 85 },
    { title: 'Patient satisfaction goals', progress: 92 },
    { title: 'Team collaboration metrics', progress: 78 },
  ];

  return (
    <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Rating display */}
      <div style={{ padding: '24px', borderRadius: '12px', backgroundColor: COLORS.background, boxShadow: SHADOWS.inset, textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: '4px' }}>
          {[...Array(5)].map((_, i) => (
            <Star
              key={i}
              size={24}
              style={{
                fill: i < Math.floor(rating) ? COLORS.primary : i < rating ? COLORS.primary : '#ddd',
                color: i < Math.floor(rating) ? COLORS.primary : i < rating ? COLORS.primary : '#ddd',
              }}
            />
          ))}
        </div>
        <p style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: COLORS.text }}>
          {rating} / {maxRating}
        </p>
      </div>

      {/* Goals list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <h3 style={{ fontSize: '14px', fontWeight: '700', color: COLORS.text, margin: 0 }}>Goals</h3>
        {goals.map((goal, i) => (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '13px', fontWeight: '500', color: COLORS.text }}>{goal.title}</span>
              <span style={{ fontSize: '12px', color: '#888' }}>{goal.progress}%</span>
            </div>
            <div
              style={{
                height: '8px',
                borderRadius: '4px',
                backgroundColor: '#e0e0e0',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${goal.progress}%`,
                  backgroundColor: goal.progress >= 80 ? COLORS.success : goal.progress >= 60 ? COLORS.primary : COLORS.warning,
                  transition: 'width 0.3s ease',
                }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Recent reviews */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <h3 style={{ fontSize: '14px', fontWeight: '700', color: COLORS.text, margin: 0 }}>Recent Reviews</h3>
        {[
          { date: '2024-12-01', reviewer: 'Jane Doe', summary: 'Strong performance this quarter. Excellent patient feedback.' },
          { date: '2024-09-15', reviewer: 'John Smith', summary: 'Good progress on goals. Consider leadership training.' },
        ].map((review, i) => (
          <div
            key={i}
            style={{
              padding: '16px',
              borderRadius: '12px',
              backgroundColor: COLORS.background,
              boxShadow: SHADOWS.inset,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <span style={{ fontSize: '12px', color: '#888' }}>{formatDate(review.date)}</span>
              <span style={{ fontSize: '12px', fontWeight: '600', color: COLORS.primary }}>by {review.reviewer}</span>
            </div>
            <p style={{ margin: 0, fontSize: '13px', color: '#555', lineHeight: '1.5' }}>{review.summary}</p>
          </div>
        ))}
      </div>

      {/* Add Review button */}
      <button
        style={{
          padding: '12px 20px',
          borderRadius: '12px',
          border: `2px solid ${COLORS.primary}`,
          backgroundColor: 'transparent',
          color: COLORS.primary,
          fontSize: '13px',
          fontWeight: '600',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
        }}
        onMouseOver={(e) => {
          e.target.style.backgroundColor = COLORS.primary + '10';
          e.target.style.boxShadow = SHADOWS.neumorphic;
        }}
        onMouseOut={(e) => {
          e.target.style.backgroundColor = 'transparent';
          e.target.style.boxShadow = 'none';
        }}
      >
        <Plus size={14} style={{ display: 'inline', marginRight: '6px' }} />
        Add Review
      </button>
    </div>
  );
};

// Tab: Leave & Time Off
const LeaveTab = ({ person }) => {
  const leaveBalances = [
    { type: 'Annual', used: 8, total: 20, color: COLORS.primary },
    { type: 'Sick', used: 2, total: 10, color: COLORS.success },
    { type: 'Personal', used: 1, total: 5, color: COLORS.warning },
  ];

  const LeaveProgressCircle = ({ used, total, color }) => {
    const percentage = (used / total) * 100;
    const radius = 45;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (percentage / 100) * circumference;

    return (
      <svg width="100" height="100" viewBox="0 0 100 100" style={{ transform: 'rotate(-90deg)' }}>
        <circle
          cx="50"
          cy="50"
          r={radius}
          fill="none"
          stroke="#e0e0e0"
          strokeWidth="4"
        />
        <circle
          cx="50"
          cy="50"
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth="4"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 0.3s ease' }}
        />
        <text
          x="50"
          y="55"
          textAnchor="middle"
          dominantBaseline="middle"
          style={{ fontSize: '18px', fontWeight: '700', fill: COLORS.text }}
        >
          {total - used}
        </text>
      </svg>
    );
  };

  return (
    <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Leave balance cards */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
        {leaveBalances.map((leave, i) => (
          <div
            key={i}
            style={{
              padding: '20px',
              borderRadius: '12px',
              backgroundColor: COLORS.background,
              boxShadow: SHADOWS.inset,
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              alignItems: 'center',
            }}
          >
            <span style={{ fontSize: '12px', fontWeight: '600', color: '#666', textTransform: 'uppercase' }}>
              {leave.type}
            </span>
            <LeaveProgressCircle used={leave.used} total={leave.total} color={leave.color} />
            <span style={{ fontSize: '11px', color: '#888' }}>
              {leave.used} / {leave.total} used
            </span>
          </div>
        ))}
      </div>

      {/* Leave history table */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <h3 style={{ fontSize: '14px', fontWeight: '700', color: COLORS.text, margin: 0 }}>Leave History</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ borderBottom: `2px solid #e0e0e0` }}>
                <th style={{ padding: '12px 0', textAlign: 'left', fontWeight: '600', color: COLORS.text }}>Date Range</th>
                <th style={{ padding: '12px 0', textAlign: 'left', fontWeight: '600', color: COLORS.text }}>Type</th>
                <th style={{ padding: '12px 0', textAlign: 'left', fontWeight: '600', color: COLORS.text }}>Status</th>
                <th style={{ padding: '12px 0', textAlign: 'center', fontWeight: '600', color: COLORS.text }}>Days</th>
              </tr>
            </thead>
            <tbody>
              {[
                { from: '2024-12-23', to: '2024-12-27', type: 'Annual', status: 'Approved', days: 5 },
                { from: '2024-11-08', to: '2024-11-08', type: 'Sick', status: 'Approved', days: 1 },
                { from: '2024-10-01', to: '2024-10-03', type: 'Personal', status: 'Pending', days: 3 },
              ].map((row, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '12px 0', color: '#555' }}>
                    {formatDate(row.from)} - {formatDate(row.to)}
                  </td>
                  <td style={{ padding: '12px 0', color: '#555' }}>{row.type}</td>
                  <td style={{ padding: '12px 0' }}>
                    <span
                      style={{
                        padding: '4px 8px',
                        borderRadius: '6px',
                        fontSize: '11px',
                        fontWeight: '600',
                        backgroundColor: row.status === 'Approved' ? COLORS.success + '20' : COLORS.warning + '20',
                        color: row.status === 'Approved' ? COLORS.success : COLORS.warning,
                      }}
                    >
                      {row.status}
                    </span>
                  </td>
                  <td style={{ padding: '12px 0', textAlign: 'center', fontWeight: '600', color: COLORS.text }}>
                    {row.days}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Request Leave button */}
      <button
        style={{
          padding: '12px 20px',
          borderRadius: '12px',
          border: `2px solid ${COLORS.primary}`,
          backgroundColor: 'transparent',
          color: COLORS.primary,
          fontSize: '13px',
          fontWeight: '600',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
        }}
        onMouseOver={(e) => {
          e.target.style.backgroundColor = COLORS.primary + '10';
          e.target.style.boxShadow = SHADOWS.neumorphic;
        }}
        onMouseOut={(e) => {
          e.target.style.backgroundColor = 'transparent';
          e.target.style.boxShadow = 'none';
        }}
      >
        <Plus size={14} style={{ display: 'inline', marginRight: '6px' }} />
        Request Leave
      </button>
    </div>
  );
};

// Tab: Documents
const DocumentsTab = ({ person }) => {
  const documents = [
    { name: 'Employment_Agreement.pdf', type: 'PDF', size: '245 KB', date: '2021-01-15' },
    { name: 'Certification_License.pdf', type: 'PDF', size: '512 KB', date: '2022-06-10' },
    { name: 'Tax_Form_W2.pdf', type: 'PDF', size: '128 KB', date: '2023-12-15' },
    { name: 'Performance_Review_2024.docx', type: 'DOC', size: '89 KB', date: '2024-12-01' },
    { name: 'Profile_Photo.jpg', type: 'Image', size: '1.2 MB', date: '2024-01-20' },
  ];

  const getFileIcon = (type) => {
    switch (type) {
      case 'PDF':
      case 'DOC':
        return <FileText size={16} />;
      case 'Image':
        return <User size={16} />;
      default:
        return <FileIcon size={16} />;
    }
  };

  return (
    <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Upload button */}
      <button
        style={{
          padding: '12px 20px',
          borderRadius: '12px',
          border: `2px solid ${COLORS.primary}`,
          backgroundColor: 'transparent',
          color: COLORS.primary,
          fontSize: '13px',
          fontWeight: '600',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          alignSelf: 'flex-start',
        }}
        onMouseOver={(e) => {
          e.target.style.backgroundColor = COLORS.primary + '10';
          e.target.style.boxShadow = SHADOWS.neumorphic;
        }}
        onMouseOut={(e) => {
          e.target.style.backgroundColor = 'transparent';
          e.target.style.boxShadow = 'none';
        }}
      >
        <Upload size={14} style={{ display: 'inline', marginRight: '6px' }} />
        Upload Document
      </button>

      {/* Documents table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr style={{ borderBottom: `2px solid #e0e0e0` }}>
              <th style={{ padding: '12px 0', textAlign: 'left', fontWeight: '600', color: COLORS.text }}>Name</th>
              <th style={{ padding: '12px 0', textAlign: 'left', fontWeight: '600', color: COLORS.text }}>Type</th>
              <th style={{ padding: '12px 0', textAlign: 'left', fontWeight: '600', color: COLORS.text }}>Date</th>
              <th style={{ padding: '12px 0', textAlign: 'left', fontWeight: '600', color: COLORS.text }}>Size</th>
              <th style={{ padding: '12px 0', textAlign: 'center', fontWeight: '600', color: COLORS.text }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {documents.map((doc, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '12px 0', display: 'flex', alignItems: 'center', gap: '8px', color: '#555' }}>
                  <span style={{ color: COLORS.primary }}>{getFileIcon(doc.type)}</span>
                  {doc.name}
                </td>
                <td style={{ padding: '12px 0', color: '#555' }}>
                  <span
                    style={{
                      padding: '2px 8px',
                      borderRadius: '4px',
                      backgroundColor: '#e0e0e0',
                      fontSize: '11px',
                      fontWeight: '600',
                      color: COLORS.text,
                    }}
                  >
                    {doc.type}
                  </span>
                </td>
                <td style={{ padding: '12px 0', color: '#555' }}>{formatDate(doc.date)}</td>
                <td style={{ padding: '12px 0', color: '#555' }}>{doc.size}</td>
                <td style={{ padding: '12px 0', textAlign: 'center' }}>
                  <button
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: COLORS.primary,
                      padding: '4px 8px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      fontSize: '12px',
                      fontWeight: '600',
                      transition: 'color 0.2s ease',
                    }}
                    onMouseOver={(e) => (e.target.style.color = COLORS.warning)}
                    onMouseOut={(e) => (e.target.style.color = COLORS.primary)}
                  >
                    <Download size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Tab: CRM Activity
const CRMActivityTab = ({ person }) => {
  const [crmData, setCrmData] = useState({ contacts: [], interactions: [], tasks: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadCRM() {
      try {
        const result = await window.zignagenixAPI.getPersonnelCRMActivity(person.id);
        if (result && !result.error) {
          setCrmData(result);
        }
      } catch (err) {
        console.warn('[Personnel] CRM cross-link not available:', err);
      } finally {
        setLoading(false);
      }
    }
    loadCRM();
  }, [person.id]);

  // Build activities from real CRM interactions, or fall back to demo
  const activities = crmData.interactions.length > 0
    ? crmData.interactions.map(i => ({
        date: i.created_at || i.interaction_date,
        type: (i.channel || i.type || 'Note').charAt(0).toUpperCase() + (i.channel || i.type || 'note').slice(1),
        description: i.notes || i.subject || i.summary || 'CRM interaction',
        contact: i.logged_by_name || 'System',
      }))
    : [
        { date: '2026-03-20', type: 'Call', description: 'Discussed Q1 goals and career development', contact: 'John Smith' },
        { date: '2026-03-15', type: 'Email', description: 'Sent performance review feedback', contact: 'Jane Doe' },
        { date: '2026-03-10', type: 'Meeting', description: 'Quarterly review and planning session', contact: 'Management Team' },
        { date: '2026-03-01', type: 'Email', description: 'Onboarding checklist completed', contact: 'HR Department' },
      ];

  const getActivityIcon = (type) => {
    switch (type) {
      case 'Call':
        return <Phone size={14} />;
      case 'Email':
        return <Mail size={14} />;
      case 'Meeting':
        return <Calendar size={14} />;
      default:
        return <Clock size={14} />;
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'Call':
        return COLORS.primary;
      case 'Email':
        return COLORS.success;
      case 'Meeting':
        return COLORS.warning;
      default:
        return '#666';
    }
  };

  return (
    <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* CRM Link Summary */}
      {loading ? (
        <div style={{ padding: 16, textAlign: 'center', color: '#ADB5BD', fontSize: 13 }}>Loading CRM data...</div>
      ) : (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 120px', padding: '12px 16px', borderRadius: 12, background: COLORS.background, boxShadow: SHADOWS.neumorphic, textAlign: 'center' }}>
            <div style={{ fontSize: 20, fontWeight: 700, color: COLORS.primary }}>{crmData.contacts.length}</div>
            <div style={{ fontSize: 11, color: '#888', fontWeight: 500 }}>Linked CRM Contacts</div>
          </div>
          <div style={{ flex: '1 1 120px', padding: '12px 16px', borderRadius: 12, background: COLORS.background, boxShadow: SHADOWS.neumorphic, textAlign: 'center' }}>
            <div style={{ fontSize: 20, fontWeight: 700, color: COLORS.success }}>{crmData.interactions.length}</div>
            <div style={{ fontSize: 11, color: '#888', fontWeight: 500 }}>CRM Interactions</div>
          </div>
          <div style={{ flex: '1 1 120px', padding: '12px 16px', borderRadius: 12, background: COLORS.background, boxShadow: SHADOWS.neumorphic, textAlign: 'center' }}>
            <div style={{ fontSize: 20, fontWeight: 700, color: COLORS.warning }}>{crmData.tasks.length}</div>
            <div style={{ fontSize: 11, color: '#888', fontWeight: 500 }}>CRM Tasks</div>
          </div>
        </div>
      )}

      {/* Activities list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {activities.map((activity, i) => (
          <div
            key={i}
            style={{
              padding: '16px',
              borderRadius: '12px',
              backgroundColor: COLORS.background,
              boxShadow: SHADOWS.inset,
              display: 'flex',
              gap: '12px',
            }}
          >
            <div
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                backgroundColor: getActivityColor(activity.type) + '20',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                color: getActivityColor(activity.type),
              }}
            >
              {getActivityIcon(activity.type)}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <span
                  style={{
                    fontSize: '12px',
                    fontWeight: '600',
                    color: getActivityColor(activity.type),
                    textTransform: 'uppercase',
                  }}
                >
                  {activity.type}
                </span>
                <span style={{ fontSize: '12px', color: '#888' }}>{formatDate(activity.date)}</span>
              </div>
              <p style={{ margin: '0 0 6px 0', fontSize: '13px', color: COLORS.text, fontWeight: '500' }}>
                {activity.description}
              </p>
              <p style={{ margin: 0, fontSize: '12px', color: '#888' }}>with {activity.contact}</p>
            </div>
          </div>
        ))}
      </div>

      {/* View in CRM button */}
      <button
        style={{
          padding: '12px 20px',
          borderRadius: '12px',
          border: `2px solid ${COLORS.primary}`,
          backgroundColor: 'transparent',
          color: COLORS.primary,
          fontSize: '13px',
          fontWeight: '600',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
        }}
        onMouseOver={(e) => {
          e.target.style.backgroundColor = COLORS.primary + '10';
          e.target.style.boxShadow = SHADOWS.neumorphic;
        }}
        onMouseOut={(e) => {
          e.target.style.backgroundColor = 'transparent';
          e.target.style.boxShadow = 'none';
        }}
      >
        View in CRM
        <ChevronRight size={14} />
      </button>
    </div>
  );
};

// Main Component
export default function ProfileModal({ person, onClose, onEdit, onSave, allPersonnel }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [isOpen, setIsOpen] = useState(true);

  if (!person || !isOpen) return null;

  const jobColor = getCategoryColor(person.jobTitle);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: User },
    { id: 'history', label: 'Employment History', icon: TrendingUp },
    { id: 'performance', label: 'Performance', icon: Star },
    { id: 'leave', label: 'Leave & Time Off', icon: Calendar },
    { id: 'documents', label: 'Documents', icon: FileText },
    { id: 'crm', label: 'CRM Activity', icon: CheckCircle },
  ];

  const handleClose = () => {
    setIsOpen(false);
    onClose?.();
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(15,23,42,0.5)',
        backdropFilter: 'blur(4px)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'flex-end',
      }}
      onClick={handleClose}
    >
      {/* Modal Panel */}
      <div
        style={{
          width: '60%',
          height: '100vh',
          backgroundColor: COLORS.background,
          boxShadow: SHADOWS.modal,
          display: 'flex',
          flexDirection: 'column',
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.3s ease',
          position: 'relative',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            border: 'none',
            backgroundColor: COLORS.background,
            boxShadow: SHADOWS.neumorphic,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: COLORS.text,
            transition: 'all 0.2s ease',
            zIndex: 10,
          }}
          onMouseOver={(e) => {
            e.target.style.backgroundColor = COLORS.primary;
            e.target.style.color = 'white';
          }}
          onMouseOut={(e) => {
            e.target.style.backgroundColor = COLORS.background;
            e.target.style.color = COLORS.text;
          }}
        >
          <X size={20} />
        </button>

        {/* Left Column - Profile Summary */}
        <div
          style={{
            width: '200px',
            padding: '24px',
            borderRight: `1px solid #e0e0e0`,
            overflowY: 'auto',
            backgroundColor: '#F8F9FA',
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
            position: 'absolute',
            height: '100%',
            left: 0,
            top: 0,
          }}
        >
          {/* Avatar */}
          <div
            style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${jobColor} 0%, ${jobColor}dd 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '18px',
              fontWeight: '700',
              boxShadow: SHADOWS.neumorphic,
              marginTop: '40px',
            }}
          >
            {getInitials(person.firstName, person.lastName)}
          </div>

          {/* Name and Title */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <h1 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: COLORS.text }}>
              {person.firstName} {person.lastName}
            </h1>
            <p style={{ margin: 0, fontSize: '12px', color: '#888', fontWeight: '500' }}>
              {person.jobTitle || 'N/A'}
            </p>
            <p style={{ margin: 0, fontSize: '12px', color: '#888' }}>
              {person.department || 'N/A'}
            </p>
          </div>

          {/* Status Badge */}
          <div
            style={{
              padding: '6px 12px',
              borderRadius: '8px',
              backgroundColor: person.status === 'Active' ? COLORS.success + '20' : COLORS.danger + '20',
              color: person.status === 'Active' ? COLORS.success : COLORS.danger,
              fontSize: '11px',
              fontWeight: '600',
              textAlign: 'center',
            }}
          >
            {person.status || 'Active'}
          </div>

          {/* Employee ID */}
          <div
            style={{
              padding: '12px',
              borderRadius: '8px',
              backgroundColor: COLORS.background,
              boxShadow: SHADOWS.inset,
              fontSize: '11px',
              textAlign: 'center',
            }}
          >
            <p style={{ margin: '0 0 4px 0', color: '#888', fontSize: '10px' }}>EMPLOYEE ID</p>
            <p style={{ margin: 0, fontWeight: '700', color: COLORS.text }}>
              {person.employeeId || 'N/A'}
            </p>
          </div>

          {/* Contact Info */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {person.email && (
              <a
                href={`mailto:${person.email}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '12px',
                  color: COLORS.primary,
                  textDecoration: 'none',
                  transition: 'color 0.2s ease',
                }}
                onMouseOver={(e) => (e.currentTarget.style.color = COLORS.warning)}
                onMouseOut={(e) => (e.currentTarget.style.color = COLORS.primary)}
              >
                <Mail size={14} />
                <span style={{ wordBreak: 'break-all' }}>{person.email}</span>
              </a>
            )}
            {person.phone && (
              <a
                href={`tel:${person.phone}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '12px',
                  color: COLORS.primary,
                  textDecoration: 'none',
                  transition: 'color 0.2s ease',
                }}
                onMouseOver={(e) => (e.currentTarget.style.color = COLORS.warning)}
                onMouseOut={(e) => (e.currentTarget.style.color = COLORS.primary)}
              >
                <Phone size={14} />
                {person.phone}
              </a>
            )}
          </div>

          {/* Hire Date & Tenure */}
          <div
            style={{
              padding: '12px',
              borderRadius: '8px',
              backgroundColor: COLORS.background,
              boxShadow: SHADOWS.inset,
              fontSize: '11px',
              textAlign: 'center',
            }}
          >
            <p style={{ margin: '0 0 4px 0', color: '#888', fontSize: '10px' }}>TENURE</p>
            <p style={{ margin: 0, fontWeight: '700', color: COLORS.text }}>
              {calculateTenure(person.hireDate)}
            </p>
            <p style={{ margin: '2px 0 0 0', fontSize: '10px', color: '#888' }}>
              since {formatDate(person.hireDate)}
            </p>
          </div>

          {/* Supervisor */}
          {person.supervisorName && (
            <div
              style={{
                padding: '12px',
                borderRadius: '8px',
                backgroundColor: COLORS.background,
                boxShadow: SHADOWS.inset,
              }}
            >
              <p style={{ margin: '0 0 4px 0', color: '#888', fontSize: '10px', fontWeight: '600' }}>SUPERVISOR</p>
              <p style={{ margin: 0, fontSize: '12px', fontWeight: '600', color: COLORS.text }}>
                {person.supervisorName}
              </p>
            </div>
          )}

          {/* Edit Profile Button */}
          <button
            onClick={onEdit}
            style={{
              padding: '12px 16px',
              borderRadius: '8px',
              backgroundColor: COLORS.primary,
              color: 'white',
              border: 'none',
              fontSize: '12px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              boxShadow: SHADOWS.neumorphic,
              transition: 'all 0.2s ease',
              marginTop: 'auto',
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#006688';
              e.target.style.transform = 'translateY(-2px)';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = COLORS.primary;
              e.target.style.transform = 'translateY(0)';
            }}
          >
            <Edit2 size={14} />
            Edit Profile
          </button>

          {/* AI Insights Button */}
          <button
            style={{
              padding: '12px 16px',
              borderRadius: '8px',
              backgroundColor: 'transparent',
              color: COLORS.primary,
              border: `1px solid ${COLORS.primary}40`,
              fontSize: '12px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              transition: 'all 0.2s ease',
              position: 'relative',
            }}
            title="AI insights coming soon"
            onMouseOver={(e) => {
              e.target.style.backgroundColor = COLORS.primary + '10';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = 'transparent';
            }}
          >
            <Brain size={14} />
            AI Insights
          </button>
        </div>

        {/* Right Column - Tabbed Content */}
        <div
          style={{
            flex: 1,
            marginLeft: '200px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* Tab Bar */}
          <div
            style={{
              display: 'flex',
              gap: '8px',
              padding: '16px 24px',
              borderBottom: `1px solid #e0e0e0`,
              overflowX: 'auto',
              backgroundColor: '#F8F9FA',
            }}
          >
            {tabs.map((tab) => {
              const TabIcon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  style={{
                    padding: '8px 14px',
                    borderRadius: '8px',
                    border: activeTab === tab.id ? `2px solid ${COLORS.primary}` : '1px solid #e0e0e0',
                    backgroundColor: activeTab === tab.id ? COLORS.primary + '10' : 'transparent',
                    color: activeTab === tab.id ? COLORS.primary : '#888',
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'all 0.2s ease',
                    whiteSpace: 'nowrap',
                  }}
                  onMouseOver={(e) => {
                    if (activeTab !== tab.id) {
                      e.target.style.backgroundColor = '#f0f0f0';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (activeTab !== tab.id) {
                      e.target.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  <TabIcon size={14} />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* Tab Content */}
          <div style={{ flex: 1, overflowY: 'auto', backgroundColor: '#F8F9FA' }}>
            {activeTab === 'overview' && <OverviewTab person={person} />}
            {activeTab === 'history' && <EmploymentHistoryTab person={person} />}
            {activeTab === 'performance' && <PerformanceTab person={person} />}
            {activeTab === 'leave' && <LeaveTab person={person} />}
            {activeTab === 'documents' && <DocumentsTab person={person} />}
            {activeTab === 'crm' && <CRMActivityTab person={person} />}
          </div>
        </div>
      </div>
    </div>
  );
}
